import { useState, useEffect } from 'react'
import './MailVerificationDialog.css'

const MailVerificationDialog = ({ mode = 'zip', parcels = [], onClose = () => {}, onConfirm = null }) => {
    const [email, setEmail] = useState('')
    const [error, setError] = useState('')
    const [isSending, setIsSending] = useState(false)
    const [turnstileToken, setTurnstileToken] = useState('')
    const [turnstileWidgetId, setTurnstileWidgetId] = useState(null)
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKey)
        return() => document.removeEventListener('keydown', onKey)
    }, [onClose])

    useEffect(() => {
        let timer = null
        const renderIfReady = () => {
            if (typeof window !== 'undefined' && window.turnstile) {
                const el = document.querySelector('.cf-turnstile')
                if (el) {
                    try {
                        if (!el.dataset._turnstileRendered) {
                            const wid = window.turnstile.render(el, {
                                sitekey: process.env.REACT_APP_CLOUDFLARE_SITE_KEY,
                                callback: (token) => setTurnstileToken(token),
                                'expired-callback': () => setTurnstileToken('')
                            })
                            setTurnstileWidgetId(wid)
                            el.dataset._turnstileRendered = '1'
                        } else {
                            // try to read existing token if available
                            try {
                                const existing = window.turnstile.getResponse?.(turnstileWidgetId)
                                if (existing) setTurnstileToken(existing)
                            } catch(e){}
                        }
                    } catch (err) {
                        console.error('turnstile.render error', err)
                    }
                }
                if (timer) {
                    clearInterval(timer)
                    timer = null
                }
            }
        }

        // try immediately, otherwise poll until the script has loaded
        renderIfReady()
        if (!window.turnstile) {
            timer = setInterval(renderIfReady, 250)
        }

        return () => {
            if (timer) clearInterval(timer)
        }
    }, [])


    const handleSend = async (e) => {
        e.preventDefault()
        setError('')

        const value = email.trim()
        if (!emailPattern.test(value)) {
            setError('Unesite važeću e‑mail adresu.')
            return
        }

        const domain = value.split('@')[1]?.toLowerCase() || ''
        const allowed = ['polj.edu.rs', 'polj.uns.ac.rs']
        if (!allowed.includes(domain)) {
            setError('Preuzimanje je dozvoljeno samo @polj.uns.ac.rs i @polj.edu.rs korisnicima.')
            return
        }

        setIsSending(true)

        if (!turnstileToken) {
            setError('Potvrdite da niste robot (Turnstile).')
            setIsSending(false)
            return
        }

        if (parcels.length === 0) {
            console.log('Nema parcela za kreiranje ZIP fajla');
            setError('Nema selektovanih parcela.');
            setIsSending(false);
            return;
        }

        const parcelIds = parcels.map(p => p?.parcela_id).filter(Boolean)

        const payload = {
            parcelIds,
            email: value,
            'cf-turnstile-response': turnstileToken
        }

        if (typeof onConfirm === 'function') {
            const endpoint = mode === 'report' ? 'send-report' : 'send-mail'
            onConfirm({
                email: value,
                parcelIds,
                turnstileToken,
                send: () => fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8085'}/api/koparcele/parcels/${endpoint}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json; charset=utf-8' },
                    body: JSON.stringify(payload)
                }).then(res => res.json())
            })
            setIsSending(false)
            onClose()
            return
        }

        // Fallback if no onConfirm provided
        try {
            const endpoint = mode === 'report' ? 'send-report' : 'send-mail'
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8085'}/api/koparcele/parcels/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; charset=utf-8' },
                body: JSON.stringify(payload)
            })

            const result = await response.json()

            if (result.success) {
                console.log('E-mail poslat:', result)
            } else {
                console.error('Greška pri slanju:', result)
                setError(result.error || 'Greška pri slanju.')
            }
            onClose()
        } catch (err) {
            console.error(err)
            setError("Error occured. Try again.")
        } finally {
            setIsSending(false)
        }
    }

    return (
        <div className="mvd-overlay" role="dialog" aria-modal="true" aria-label="Mail verification dialog">
            <div className="mvd-dialog">
                <header className="mvd-header">
                    <h3>{mode === 'report' ? 'Potvrda e-pošte za izveštaj' : 'Potvrda e-pošte za preuzimanje'}</h3>
                    <button className="mvd-close" onClick={onClose} aria-label="Zatvori">x</button>
                </header>
                <form className="mvd-body" onSubmit={handleSend}>
                    <p className="mvd-desc">
                        {mode === 'report' ? 'Unesite vašu e‑mail adresu na koju će stići .pdf fajl.' : 'Unesite vašu e‑mail adresu na koju će stići .zip fajl.'}
                        {parcels && parcels.length > 0 && (
                            <span className="mvd-parcels-count"> Selektovano parcela: {parcels.length}</span>
                        )}
                    </p>
                    <label className="mvd-label">
                        E-mail adresa 
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder='example@polj.edu.rs' className="mvd-input" disabled={isSending} required
                        />
                    </label>
                    <div className="cf-turnstile"></div>
                    {error && <div className="mvd-error" role="alert">{error}</div>}
                    <footer className="mvd-actions">
                        <button type="button" className="mvd-btn mvd-cancel" onClick={onClose} disabled={isSending}>
                            Otkaži
                        </button>
                        <button type="submit" className="mvd-btn mvd-send" disabled={isSending}>
                            {isSending ? "Šaljem..." : (mode === 'report' ? "Pošalji izveštaj" : "Pošalji link")}
                        </button>
                    </footer>
                </form>
            </div>
        </div>
    )

};

export default MailVerificationDialog
